import { emitLog, getCommonAttributes, getMeter } from './telemetrySdk';
import type { Attributes, Counter } from '@opentelemetry/api';

// Lazily-created counter for unhandled browser errors. Created on first report
// once a Meter is available; a no-op before the SDK is initialized.
let errorCounter: Counter | null = null;

function getErrorCounter(): Counter | null {
  if (!errorCounter) {
    const meter = getMeter();
    if (meter) {
      errorCounter = meter.createCounter('browser.error.count', {
        description: 'Count of unhandled browser exceptions and promise rejections',
        unit: '1',
      });
    }
  }
  return errorCounter;
}

/** The constructor name for an Error (e.g. "TypeError"), else the primitive `typeof`. */
function errorTypeOf(error: unknown): string {
  if (error instanceof Error) {
    return error.constructor?.name || 'Error';
  }
  if (typeof error === 'object' && error !== null) {
    return error.constructor?.name || 'Object';
  }
  return typeof error;
}

/** The fullest message we can produce for the log body: the stack if present. */
function bodyOf(error: unknown): string {
  if (error instanceof Error) {
    return error.stack || `${error.name}: ${error.message}`;
  }
  return String(error);
}

/** Low-cardinality attributes shared by the error counter and log record. */
function errorAttributes(error: unknown): Attributes {
  return { ...getCommonAttributes(), 'error.type': errorTypeOf(error) };
}

/**
 * Increment `browser.error.count` by 1 with low-cardinality `{ route, error.type }`
 * attributes (route from the telemetry common attributes), without emitting a
 * log record. Use this when the error is already being logged elsewhere (e.g. a
 * React ErrorBoundary that calls `logError`). No-op before the SDK is initialized.
 */
export function incrementBrowserErrorCount(error: unknown): void {
  getErrorCounter()?.add(1, errorAttributes(error));
}

/**
 * Record a captured error as both a metric increment and a log record:
 *   - `browser.error.count` += 1 with low-cardinality `{ route, error.type }`, and
 *   - an error-level OTel log record whose body carries the (high-cardinality)
 *     stack trace, dimensioned with the same attributes.
 *
 * Safe to call before the SDK is initialized — both sinks no-op.
 */
export function reportError(error: unknown): void {
  const attributes = errorAttributes(error);
  getErrorCounter()?.add(1, attributes);
  emitLog('error', bodyOf(error), attributes);
}

/**
 * Install global handlers that funnel uncaught exceptions and unhandled promise
 * rejections through {@link reportError}. Uses `addEventListener` so it composes
 * with any handlers the app already has rather than clobbering them. Returns an
 * unregister function that removes both listeners.
 */
export function registerGlobalErrorHandlers(): () => void {
  const onError = (event: ErrorEvent) => reportError(event.error ?? event.message);
  const onRejection = (event: PromiseRejectionEvent) => reportError(event.reason);

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Visible for testing only. Clears the cached counter so the next report re-creates it. */
export function resetErrorCaptureForTesting(): void {
  errorCounter = null;
}
